import { useI18n } from '@ecom/i18n';
import { Card } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface QuickAction {
  readonly label: string;
  readonly href: string;
  readonly icon: ReactNode;
}

const PLUS_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const TAG_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 12.5 12.5 20a1.5 1.5 0 0 1-2.12 0L4 13.62a1.5 1.5 0 0 1 0-2.12L11.5 4H18a2 2 0 0 1 2 2v6.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="15.5" cy="8.5" r="1.25" fill="currentColor" />
  </svg>
);

const BOX_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3.5 8 12 3.5 20.5 8v8L12 20.5 3.5 16V8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M3.5 8 12 12.5 20.5 8M12 12.5V20.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const LIST_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * "Bloc Actions rapides" — Sprint UX/UI Merchant Dashboard V2. Exactly the
 * four actions the supervisor validated, all real routes this app already
 * serves: "+ Commande" was explicitly refused ("cette fonctionnalité
 * n'existe pas") and replaced with "Voir les commandes".
 */
export function QuickActions(): ReactElement {
  const { t } = useI18n();
  const actions: readonly QuickAction[] = [
    {
      label: t('merchant.dashboard.quickActions.newProduct'),
      href: '/products/new',
      icon: PLUS_ICON,
    },
    {
      label: t('merchant.dashboard.quickActions.newCoupon'),
      href: '/promotions?new=1',
      icon: TAG_ICON,
    },
    { label: t('merchant.nav.inventory'), href: '/inventory', icon: BOX_ICON },
    { label: t('merchant.dashboard.quickActions.viewOrders'), href: '/orders', icon: LIST_ICON },
  ];

  return (
    <Card className="p-5">
      <h3 className="font-display text-sm font-bold text-ink">
        {t('merchant.dashboard.quickActions.title')}
      </h3>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {actions.map((action) => (
          <Link
            key={action.label}
            to={action.href}
            className="flex flex-col items-start gap-2 rounded-lg border border-line px-3 py-3 text-sm font-medium text-ink transition-colors hover:border-accent/50 hover:bg-surface2"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accentSoft text-accentInk">
              {action.icon}
            </span>
            {action.label}
          </Link>
        ))}
      </div>
    </Card>
  );
}
